/**
 * BorderGenerator.ts
 * Integrates xAI (Grok) API for generating custom photobooth borders
 * and provides precise transparency window cutouts (both Inset Mask & BFS Flood Key).
 */

export interface BorderPromptConfig {
  theme: string;
  topElements: string;
  sideElements: string;
  bottomElements: string;
  orientation: 'portrait' | 'landscape';
}

export interface TransparencyWindowConfig {
  mode: 'inset' | 'bfs-key';
  topInset: number;
  bottomInset: number;
  leftInset: number;
  rightInset: number;
  cornerRadius: number;
  feather: number;
  keyThreshold: number; // For BFS key (0-100)
  innerStroke: boolean;
  innerStrokeColor: string;
  innerStrokeWidth: number;
}

export class BorderGenerator {
  public static readonly DEFAULT_GROK_API_KEY =
    'xai-ZiKxAn3do61HsxszHFsYqw23jVk2Vza4A974iW08igYMXV95COqBFjpujt4Mf1ZNQiMEA3xDNNIumr5D';

  public static getStoredApiKey(): string {
    return localStorage.getItem('grok_api_key') || this.DEFAULT_GROK_API_KEY;
  }

  public static setStoredApiKey(key: string) {
    localStorage.setItem('grok_api_key', key.trim());
  }

  /**
   * Generates a photobooth overlay border using Grok (xAI) imagine-image API
   */
  public static async generateBorderImage(
    config: BorderPromptConfig,
    apiKey?: string
  ): Promise<string> {
    const key = (apiKey || this.getStoredApiKey()).trim();
    if (!key) {
      throw new Error('Please enter your xAI (Grok) API key.');
    }

    const aspectRatio = config.orientation === 'portrait' ? '9:16' : '16:9';

    // Construct high-precision photobooth frame prompt
    const promptParts = [
      `Professional photobooth overlay frame border graphics. Theme: ${config.theme}.`,
      config.topElements ? `Top header border: ${config.topElements}.` : '',
      config.sideElements ? `Left and right side borders: ${config.sideElements}.` : '',
      config.bottomElements ? `Bottom footer border: ${config.bottomElements}.` : '',
      `The center MUST be a large solid pure black empty rectangular opening window framed by the decorative borders so guests can be seen through it. High detail commercial photobooth frame overlay, no watermark, crisp edges.`
    ].filter(Boolean);

    const fullPrompt = promptParts.join(' ');

    const payload = {
      model: 'grok-imagine-image',
      prompt: fullPrompt,
      aspect_ratio: aspectRatio,
      response_format: 'b64_json',
      n: 1
    };

    const response = await fetch('https://api.x.ai/v1/images/generations', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${key}`
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      let errMsg = `Grok API error (${response.status})`;
      try {
        const errJson = await response.json();
        if (errJson?.error?.message) {
          errMsg = errJson.error.message;
        } else if (errJson?.message) {
          errMsg = errJson.message;
        }
      } catch (_) {}
      throw new Error(errMsg);
    }

    const data = await response.json();
    const item = data?.data?.[0];

    if (item?.b64_json) {
      return `data:image/jpeg;base64,${item.b64_json}`;
    } else if (item?.url) {
      return item.url;
    } else {
      throw new Error('Grok API did not return an image.');
    }
  }

  /**
   * Applies the transparency window to the border canvas.
   * Target: exact dimensions (e.g. 1080x1920 or 1920x1080).
   */
  public static processTransparencyWindow(
    sourceImg: CanvasImageSource,
    targetWidth: number,
    targetHeight: number,
    config: TransparencyWindowConfig
  ): HTMLCanvasElement {
    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const ctx = canvas.getContext('2d', { willReadFrequently: true })!;

    // 1. Draw source image scaled to exact dimensions
    ctx.drawImage(sourceImg, 0, 0, targetWidth, targetHeight);

    if (config.mode === 'bfs-key') {
      // Method B: BFS Color-Key from center (matching process_borders.py)
      this.applyBFSKeyCutout(ctx, targetWidth, targetHeight, config.keyThreshold, config.feather);
    } else {
      // Method A: Inset Window Mask (adjustable margins + rounded corners)
      this.applyInsetWindowCutout(ctx, targetWidth, targetHeight, config);
    }

    return canvas;
  }

  /**
   * Cuts out a rounded or rectangular transparency window using configurable insets
   */
  private static applyInsetWindowCutout(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    config: TransparencyWindowConfig
  ) {
    const left = Math.max(0, config.leftInset);
    const top = Math.max(0, config.topInset);
    const right = Math.max(0, config.rightInset);
    const bottom = Math.max(0, config.bottomInset);

    const winW = Math.max(50, width - left - right);
    const winH = Math.max(50, height - top - bottom);
    const radius = Math.min(config.cornerRadius, Math.min(winW, winH) / 2);

    ctx.save();
    // Use destination-out to clear the window to 100% transparent alpha
    ctx.globalCompositeOperation = 'destination-out';

    if (config.feather > 0) {
      ctx.shadowColor = 'black';
      ctx.shadowBlur = config.feather * 2;
    }

    ctx.beginPath();
    ctx.roundRect(left, top, winW, winH, radius);
    ctx.fillStyle = '#000000';
    ctx.fill();
    ctx.restore();

    // Optional decorative inner stroke around the window
    if (config.innerStroke && config.innerStrokeWidth > 0) {
      ctx.save();
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = config.innerStrokeColor || '#ffffff';
      ctx.lineWidth = config.innerStrokeWidth;
      ctx.beginPath();
      ctx.roundRect(left, top, winW, winH, radius);
      ctx.stroke();
      ctx.restore();
    }
  }

  /**
   * BFS flood-fill from center coordinate (cx, cy) to clear dark center opening
   * to alpha transparent (matching process_borders.py)
   */
  private static applyBFSKeyCutout(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    threshold: number = 24,
    feather: number = 2
  ) {
    const imgData = ctx.getImageData(0, 0, width, height);
    const data = imgData.data;

    const cx = Math.floor(width / 2);
    const cy = Math.floor(height / 2);

    // Visited bitset (Uint8Array)
    const visited = new Uint8Array(width * height);
    const queue = new Int32Array(width * height);
    let head = 0;
    let tail = 0;

    const centerIdx = cy * width + cx;
    queue[tail++] = centerIdx;
    visited[centerIdx] = 1;

    // Neighbor offsets [dx, dy]
    const dx = [1, -1, 0, 0];
    const dy = [0, 0, 1, -1];

    while (head < tail) {
      const curr = queue[head++];
      const x = curr % width;
      const y = Math.floor(curr / width);

      for (let i = 0; i < 4; i++) {
        const nx = x + dx[i];
        const ny = y + dy[i];

        if (nx >= 0 && nx < width && ny >= 0 && ny < height) {
          const nIdx = ny * width + nx;
          if (visited[nIdx] === 0) {
            const pIdx = nIdx * 4;
            const r = data[pIdx];
            const g = data[pIdx + 1];
            const b = data[pIdx + 2];
            const maxC = Math.max(r, g, b);

            if (maxC <= threshold) {
              visited[nIdx] = 1;
              queue[tail++] = nIdx;
            }
          }
        }
      }
    }

    // Set alpha = 0 for all flood-filled center pixels
    for (let i = 0; i < visited.length; i++) {
      if (visited[i] === 1) {
        data[i * 4 + 3] = 0;
      }
    }

    ctx.putImageData(imgData, 0, 0);

    // Apply feathering if requested
    if (feather > 0) {
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = width;
      tempCanvas.height = height;
      const tCtx = tempCanvas.getContext('2d')!;
      tCtx.drawImage(ctx.canvas, 0, 0);

      ctx.clearRect(0, 0, width, height);
      ctx.save();
      ctx.filter = `blur(${feather}px)`;
      ctx.drawImage(tempCanvas, 0, 0);
      ctx.restore();
    }
  }

  /**
   * Adapts a portrait border to landscape (1920x1080) by letterboxing/framing
   * with consistent transparency window
   */
  public static adaptToLandscape(
    portraitCanvas: HTMLCanvasElement,
    config: TransparencyWindowConfig
  ): HTMLCanvasElement {
    const landW = 1920;
    const landH = 1080;
    const canvas = document.createElement('canvas');
    canvas.width = landW;
    canvas.height = landH;
    const ctx = canvas.getContext('2d')!;

    // Scale & stretch the decorative border textures to fill 1920x1080
    ctx.drawImage(portraitCanvas, 0, 0, landW, landH);

    // Apply matching landscape window cutout (with proportional insets)
    const landscapeConfig: TransparencyWindowConfig = {
      ...config,
      topInset: Math.round(config.topInset * (landH / 1920) * 1.4),
      bottomInset: Math.round(config.bottomInset * (landH / 1920) * 1.4),
      leftInset: Math.round(config.leftInset * (landW / 1080) * 0.9),
      rightInset: Math.round(config.rightInset * (landW / 1080) * 0.9)
    };

    if (config.mode === 'inset') {
      this.applyInsetWindowCutout(ctx, landW, landH, landscapeConfig);
    }

    return canvas;
  }
}
